/**
 * TrackingCodes
 * Čita tracking podešavanja iz Supabase i dinamički ubacuje skripte u DOM.
 * Podržava: Google Analytics 4, Google Tag Manager, Meta/Facebook Pixel,
 * TikTok Pixel, i proizvoljne head/body skripte.
 */
import { getSettings } from '../lib/cms'
import { isSupabaseConfigured } from '../lib/supabase'

function injectScript(html: string, target: 'head' | 'body') {
  const container = document.createElement('div')
  container.innerHTML = html
  const scripts = Array.from(container.querySelectorAll('script'))
  scripts.forEach(oldScript => {
    const newScript = document.createElement('script')
    Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value))
    if (oldScript.src) {
      newScript.src = oldScript.src
    } else {
      newScript.textContent = oldScript.textContent
    }
    document[target].appendChild(newScript)
  })
  // Inject non-script nodes (noscript, etc.)
  Array.from(container.childNodes).forEach(node => {
    if ((node as Element).tagName?.toLowerCase() !== 'script') {
      document[target].appendChild(node.cloneNode(true))
    }
  })
}

function injectGA4(id: string) {
  if (!id || document.querySelector(`script[src*="${id}"]`)) return
  const tag = document.createElement('script')
  tag.async = true
  tag.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
  document.head.appendChild(tag)

  const init = document.createElement('script')
  init.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${id}');
  `
  document.head.appendChild(init)
}

function injectGTM(id: string) {
  if (!id || document.querySelector(`script[src*="${id}"]`)) return
  const script = document.createElement('script')
  script.textContent = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${id}');
  `
  document.head.appendChild(script)

  // GTM noscript in body
  const noscript = document.createElement('noscript')
  const iframe = document.createElement('iframe')
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`
  iframe.height = '0'
  iframe.width = '0'
  iframe.style.display = 'none'
  iframe.style.visibility = 'hidden'
  noscript.appendChild(iframe)
  document.body.insertBefore(noscript, document.body.firstChild)
}

function injectFBPixel(id: string) {
  if (!id || (window as any).fbq) return
  const script = document.createElement('script')
  script.textContent = `
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
    (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    fbq('init','${id}');fbq('track','PageView');
  `
  document.head.appendChild(script)
}

function injectTikTokPixel(id: string) {
  if (!id || (window as any).ttq) return
  const script = document.createElement('script')
  script.textContent = `
    !function(w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
      ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];
      ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
      for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
      ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
      ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=i;ttq._t=ttq._t||{};ttq._t[e]=+new Date;
      ttq._o=ttq._o||{};ttq._o[e]=n||{};
      var o=document.createElement("script");o.type="text/javascript";o.async=!0;o.src=i+"?sdkid="+e+"&lib="+t;
      var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
      ttq.load('${id}');ttq.page();
    }(window, document, 'ttq');
  `
  document.head.appendChild(script)
}

export default function TrackingCodes() {
  useEffect(() => {
    if (!isSupabaseConfigured) return

    getSettings().then(settings => {
      if (settings.google_analytics_id)   injectGA4(settings.google_analytics_id)
      if (settings.google_tag_manager_id) injectGTM(settings.google_tag_manager_id)
      if (settings.facebook_pixel_id)     injectFBPixel(settings.facebook_pixel_id)
      if (settings.tiktok_pixel_id)       injectTikTokPixel(settings.tiktok_pixel_id)
      if (settings.custom_head_scripts)   injectScript(settings.custom_head_scripts, 'head')
      if (settings.custom_body_scripts)   injectScript(settings.custom_body_scripts, 'body')
    })
  }, [])

  return null
}
